/* eslint-disable react/prop-types */
import React from 'react';
import { Form, ProgressBar, Spinner } from 'react-bootstrap';
import {
    useTable,
    useSortBy,
    usePagination,
    useRowSelect,
    useGlobalFilter
} from 'react-table';



const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();

        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return (
            <Form.Check
                type="checkbox"
                className="form-check fs-0 mb-0 d-flex align-items-center"
            >
                <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
            </Form.Check>
        );
    }
);

const AdvanceTableWrapper = ({
    children,
    columns,
    data,
    sortable,
    selection,
    pageCount: controlledPageCount,
    selectionColumnWidth,
    initialPage = 0,
    fetchData = null,
    isLoading = false,
    perPage = 10,
    initialSortBy = []
}) => {
    columns.forEach(x => {
        x.disableSortBy = x.sort === undefined ? true : !x.sort;
    });
    const {
        getTableProps,
        headers,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        setPageSize,
        gotoPage,
        pageCount,
        state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy },
        setGlobalFilter
    } = useTable(
        {
            columns,
            data: data,
            disableSortBy: !sortable,
            manualSortBy: fetchData !== null,
            initialState: { pageIndex: initialPage, pageSize: perPage, sortBy: initialSortBy },
            manualPagination: fetchData !== null,
            pageCount: controlledPageCount
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            if (selection) {
                hooks.visibleColumns.push(columns => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        ),
                        headerProps: {
                            style: {
                                maxWidth: selectionColumnWidth
                            }
                        },
                        cellProps: {
                            style: {
                                maxWidth: selectionColumnWidth
                            }
                        },
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        )
                    },
                    ...columns
                ]);
            }
        }
    );
    React.useEffect(() => {
        if (fetchData) {
            fetchData({ page: pageIndex, size: pageSize, sortBy: sortBy });
        }
    }, [fetchData, pageIndex, pageSize, sortBy]);

    const [delayedLoading, setDelayedLoading] = React.useState(false);
    React.useEffect(() => {
        let timer;
        if (isLoading) {
            setDelayedLoading(true);
        } else {
            timer = setTimeout(() => {
                setDelayedLoading(false);
            }, 300); 
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isLoading]);

    const recursiveMap = children => {
        return React.Children.map(children, child => {
            if (child.props?.children) {
                return React.cloneElement(child, {
                    children: recursiveMap(child.props.children)
                });
            } else {
                if (child.props?.table) {
                    return React.cloneElement(child, {
                        ...child.props,
                        getTableProps,
                        headers,
                        page,
                        prepareRow,
                        canPreviousPage,
                        canNextPage,
                        nextPage,
                        previousPage,
                        gotoPage,
                        pageCount,
                        pageIndex,
                        selectedRowIds,
                        pageSize,
                        setPageSize,
                        globalFilter,
                        setGlobalFilter
                    });
                } else {
                    return child;
                }
            }
        });
    };

    return (
        <div style={{ position: 'relative' }}>
            {delayedLoading && (
                <div
                    className="fade show"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                        transition: 'opacity 0.8s ease-in-out'
                    }}
                >
                    <Spinner
                        animation="border"
                        variant="primary"
                        style={{ width: '3rem', height: '3rem' }}
                    />
                </div>
            )}
            {recursiveMap(children)}
        </div>
    );
};

export default AdvanceTableWrapper;
